import { Button } from '@progress/kendo-react-buttons'
import React, { useEffect, useState } from 'react'
import trialService from '../../services/trial.service';
import useSwal from '../../hooks/useSwal';
import { SweetAlertOptions } from 'sweetalert2';
import { Error } from '@progress/kendo-react-labels';
import { CustomerTrialDetail } from '../../types/customer/CustomerTrialDetail';
import LoadingOverlay from '../../components/LoadingOverlay';
import './_Trial.scss';
import customerService from '../../services/customer.service';
import useAuth from '../../hooks/useAuth';
import { Customer, CustomerLicensePackageCount } from '../../types/customer';
import { Loader } from '@progress/kendo-react-indicators';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../../hooks/useTranslation';
import useLocale from '../../hooks/useLocale';

function Trial() {
    const trans = useTranslation("UpgradeLicence");
    const localeCtx = useLocale();
    const swal = useSwal();
    const auth = useAuth();
    const navigate = useNavigate();
    const [upgradeLicenseLoader, setUpgradeLicenseLoader] = useState<boolean>(false);
    const [upgradeLicenseError, setUpgradeLicenseError] = useState<string>("");
    const [customerTrialInfo, setCustomerTrialInfo] = useState<CustomerTrialDetail>();
    const [customerTrialInfoLoader, setCustomerTrialInfoLoader] = useState<boolean>(false);
    const [customerInfo, setCustomerInfo] = useState<Customer>();
    const [customerInfoLoader, setCustomerInfoLoader] = useState<boolean>(false);
    const [trialLicensePackageCount, setTrialLicensePackageCount] = useState<number>(0);

    useEffect(() => {
        if (!localeCtx?.selectedLocale?.current.componentTranslations["UpgradeLicence"]) {
            trans.fetchTranslations("UpgradeLicence");
        }
    }, [localeCtx?.selectedLocale]);

    useEffect(() => {
        getCustomerTrialDetails();
        getCustomerInfo();
    }, [])

    const getCustomerTrialDetails = async () => {
        setCustomerTrialInfoLoader(true);
        try {
            const res = await trialService.getCustomerTrialDetails(auth?.tokenPayload?.CustomerId ?? 0);
            setCustomerTrialInfo(res);
        } catch (error) {
            setCustomerTrialInfo(undefined);
        } finally {
            setCustomerTrialInfoLoader(false);
        }
    }

    const getCustomerInfo = async () => {
        try {
            setCustomerInfoLoader(true);
            const customerDetails = await customerService.getCustomerDetailsById(auth?.tokenPayload?.CustomerId ?? 0);
            setCustomerInfo(customerDetails);
            setTrialLicensePackageCount(customerDetails.licensePackages?.reduce((accumulator: number, license: CustomerLicensePackageCount) => {
                return license.purchased + accumulator
            }, 0) ?? 0);
        } catch (error) {
            console.log(error);
        }
        finally {
            setCustomerInfoLoader(false);
        }
    }

    const upgradeLicense = async () => {
        try {
            setUpgradeLicenseLoader(true);
            await trialService.upgradeLicense(auth?.tokenPayload?.CustomerId ?? 0);
            const swalOptions: SweetAlertOptions = {
                icon: "success",
                title: "Upgrade completed",
                text: "All licences have been upgraded to Ultimate IQ.",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "O.K",
            };
            await swal.fire(swalOptions);
            sessionStorage.setItem("isLicenseUpgradeButtonSessionState", JSON.stringify(false));
            getCustomerTrialDetails();
        } catch (err) {
            let upgradeLicenceFailureOne = trans.fetchLabelKeyTranslation("UpgradeLicenceFailureOne", "At this time, we are unable to upgrade the licence to Ultimate IQ.");
            let upgradeLicenceFailureTwo = trans.fetchLabelKeyTranslation("UpgradeLicenceFailureTwo", "Please try again later, as the issue may be temporary. If the problem persists, do not hesitate to contact our support team for further assistance.")
            setUpgradeLicenseError(`${upgradeLicenceFailureOne}\n${upgradeLicenceFailureTwo} `);
        } finally {
            setUpgradeLicenseLoader(false);
        }
    }

    return (
        <div className='h-100 upgrade-section'>
            {upgradeLicenseError ? (
                <div className='h-100 d-flex align-item-center'>
                    <div className='upgrade text-center'>
                        <h3 className='text-center'>{trans.fetchLabelKeyTranslation("OopsTxt", "oops!")}</h3>
                        <Error>
                            <div style={{ whiteSpace: "pre-line" }}>
                                <p className='text-center mb-4 w-100 fs-14'>{upgradeLicenseError}</p>
                            </div>
                        </Error>
                        <Button onClick={() => navigate("/home")} className={`btn bg-primary text-white mt-3`}>{trans.fetchLabelKeyTranslation("BackToHomeBtn", "Back to Home")}</Button>
                    </div>
                </div>
            ) : (
                customerTrialInfoLoader ? (
                    <LoadingOverlay
                        customStyle={{ position: "fixed", marginTop: "55px" }}
                        themeColor={"light"}
                        size={"medium"}
                        loadingText={trans.fetchLabelKeyTranslation("LoadingTxt", "Fetching licence upgrade status")}
                    />
                ) : (
                    <div className='h-100'>
                        {customerTrialInfo?.isAlreadyUpgraded ? (
                            <div className='h-100 d-flex align-item-center'>
                                <div className='upgrade text-center'>
                                    <h3 className='text-center'>{trans.fetchLabelKeyTranslation("CongratsTxt", "Congratulations!")}</h3>
                                    <p className='text-center mb-4 w-100 fs-14'>{trans.fetchLabelKeyTranslation("UpgradeLicenceSuccess", "Your licence has been successfully upgraded to Ultimate IQ. You now have full access to all Ultimate IQ features.")}</p>
                                    <Button onClick={() => navigate("/home")} className={`btn bg-primary text-white mt-3`}>{trans.fetchLabelKeyTranslation("BackToHomeBtn", "Back to Home")}</Button>
                                </div>
                            </div>
                        ) : (
                            customerInfoLoader ? (
                                <div className='d-flex justify-content-center'>
                                    <Loader />
                                </div>
                            ) : (
                                !customerInfo ? (
                                    <div className='h-100 d-flex align-item-center'>
                                        <div className='upgrade text-center'>
                                            <h3 className='text-center'>{trans.fetchLabelKeyTranslation("OopsTxt", "oops!")}</h3>
                                            <Error>
                                                <p className='text-center mb-4 w-100 fs-14'>{trans.fetchLabelKeyTranslation("LicenceDetailFetchError", "We’re unable to retrieve your current licence details at this time. Please try again later.")}</p>
                                            </Error>
                                            <Button onClick={() => navigate("/home")} className={`btn bg-primary text-white mt-3`}>{trans.fetchLabelKeyTranslation("BackToHomeBtn", "Back to Home")}</Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className='h-100 d-flex align-item-center'>
                                        <div className='upgrade'>
                                            <h3 className='text-center'>To continue using advanced AI capabilities</h3>
                                            <p className='text-center text-muted'>
                                                Contact your service provider for pricing information
                                                <br/>
                                                Super Admin user permissions are required
                                                </p>
                                            <p className='text-center mb-4'><b>{trans.fetchLabelKeyTranslation("UpgradeLicencePageTitleThree", "You require")} {trialLicensePackageCount} {trans.fetchLabelKeyTranslation("UpgradeLicencePageTitleFour", "Ultimate IQ licences")}</b></p>
                                            <div className='card'>
                                                <div className='card-body p-0'>
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th></th>
                                                                <th>{trans.fetchLabelKeyTranslation("TableColumnOne", "Current Licences")}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {customerInfo.licensePackages?.map((license: CustomerLicensePackageCount) => {
                                                                return (
                                                                    license.purchased !== 0 && (
                                                                        <tr key={license.id}>
                                                                            <td>{license.name}</td>
                                                                            <td>{license.purchased}</td>
                                                                        </tr>
                                                                    )
                                                                )
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className='card-footer '>
                                                    <Button onClick={() => navigate("/home")} className={`btn bg-secondary text-white`}>{trans.fetchLabelKeyTranslation("CancelBtn", "Cancel")}</Button>
                                                    <Button onClick={upgradeLicense} disabled={upgradeLicenseLoader} className={`btn bg-primary text-white`}>{trans.fetchLabelKeyTranslation("UpgradeBtn", "Confirm Upgrade")}</Button>
                                                </div>
                                            </div>
                                            <div className='py-4'>&nbsp;</div>
                                        </div>
                                    </div>
                                )
                            )
                        )}
                    </div>
                )
            )}
        </div>
    )
}

export default Trial
